import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Activity, Clock, User, Building } from 'lucide-react';
import { formatCATDateTime } from '../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL;

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});

  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    loadLogs();
  }, [page]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/activity-logs`, {
        params: { page, page_size: 10 },
        ...axiosConfig
      });
      
      setLogs(response.data.logs);
      setTotalPages(response.data.total_pages);
      setPagination(response.data);
    } catch (error) {
      console.error('Error loading activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return formatCATDateTime(timestamp);
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'login':
        return 'bg-blue-100 text-blue-700';
      case 'claim_submitted':
        return 'bg-green-100 text-green-700';
      case 'deposit_added':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login':
        return <User className="w-4 h-4" />;
      case 'claim_submitted':
        return <Activity className="w-4 h-4" />;
      case 'deposit_added':
        return <Building className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  // Split logs into two columns
  const midPoint = Math.ceil(logs.length / 2);
  const leftColumnLogs = logs.slice(0, midPoint);
  const rightColumnLogs = logs.slice(midPoint);

  const LogItem = ({ log, index }) => (
    <div
      key={index}
      className="flex items-start sm:items-center justify-between p-2 rounded hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
    >
      <div className="flex items-start sm:items-center gap-2 flex-1 min-w-0">
        <div className={`p-1 sm:p-1.5 rounded flex-shrink-0 ${getActionColor(log.action)}`}>
          {getActionIcon(log.action)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            <span className="font-medium text-xs sm:text-sm text-gray-900 truncate">{log.username}</span>
            <span className={`px-1 sm:px-1.5 py-0.5 rounded text-xs font-medium ${getActionColor(log.action)}`}>
              {log.action.replace('_', ' ')}
            </span>
          </div>
          <p className="text-xs text-gray-600 truncate">{log.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap ml-1 sm:ml-2 flex-shrink-0">
        <Clock className="w-3 h-3 hidden sm:block" />
        <span className="text-xs">{formatDate(log.timestamp)}</span>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader className="p-3 sm:p-4 pb-2 sm:pb-3">
        <CardTitle className="flex items-center justify-between text-sm sm:text-base">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Recent Activity
          </div>
          <span className="text-xs font-normal text-gray-500">(CAT)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-2 sm:p-4">
        {loading ? (
          <div className="text-center py-4 text-sm text-gray-500">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-4 text-sm text-gray-500">No activity logs found</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4">
              {/* Left Column */}
              <div className="space-y-1">
                {leftColumnLogs.map((log, index) => (
                  <LogItem key={`left-${index}`} log={log} index={index} />
                ))}
              </div>
              {/* Right Column */}
              <div className="space-y-1">
                {rightColumnLogs.map((log, index) => (
                  <LogItem key={`right-${index}`} log={log} index={index} />
                ))}
              </div>
            </div>

            {/* Compact Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t">
                <span className="text-xs text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={!pagination.has_prev || loading}
                    className="h-7 px-2"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={!pagination.has_next || loading}
                    className="h-7 px-2"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityLogs;
